import { useEffect, useState } from "react";
import { getTodasGuias, getMaterialCategoria, type GuiaPublica, type MaterialPublico } from "../lib/api";
import { PrivateLayout } from "../components/PrivateLayout";
import { Reveal } from "../components/Reveal";
import { CATEGORIES } from "../lib/categories";
import { parseUtcDate } from "../lib/time";

function isNew(created_at: string): boolean {
  return Date.now() - parseUtcDate(created_at).getTime() < 7 * 24 * 60 * 60 * 1000;
}

export default function Biblioteca() {
  const [activeTab, setTab]    = useState(CATEGORIES[0].id);
  const [guias, setGuias]      = useState<GuiaPublica[]>([]);
  const [material, setMaterial] = useState<MaterialPublico[]>([]);
  const [loadingG, setLoadingG] = useState(true);
  const [loadingM, setLoadingM] = useState(false);
  const [total, setTotal]      = useState(0);

  // Load all guides once
  useEffect(() => {
    setLoadingG(true);
    getTodasGuias()
      .then((r) => {
        if (r.ok) { setGuias(r.data.guias); setTotal(r.data.total); }
      })
      .finally(() => setLoadingG(false));
  }, []);

  // Load material for active tab
  useEffect(() => {
    setLoadingM(true);
    getMaterialCategoria(activeTab)
      .then((r) => setMaterial(r.ok ? r.data.material : []))
      .catch(() => setMaterial([]))
      .finally(() => setLoadingM(false));
  }, [activeTab]);

  const catGuias = guias.filter((g) => g.categoria === activeTab);
  const activeCat = CATEGORIES.find((c) => c.id === activeTab)!;

  return (
    <PrivateLayout>
      <Reveal>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 30, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
            Tu biblioteca
          </h1>
          <p style={{ fontSize: 15, color: "var(--muted-2)", margin: 0 }}>
            {loadingG ? "Cargando…" : `${total} recurso${total !== 1 ? "s" : ""} disponibles · guías, ejercicios y plantillas`}
          </p>
        </div>
      </Reveal>

      {/* filter chips */}
      <Reveal delay={1}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 28 }}>
          {CATEGORIES.map((c) => {
            const count = guias.filter((g) => g.categoria === c.id).length;
            const active = activeTab === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setTab(c.id)}
                className={`chip${active ? " chip--active" : ""}`}
                style={{ border: active ? "none" : undefined }}
              >
                {!active && <span className="chip__dot" style={{ background: c.dot }} />}
                {c.label}
                {count > 0 && (
                  <span style={{
                    fontSize: 11, background: active ? "rgba(250,245,238,.18)" : "var(--sage-soft)",
                    color: active ? "var(--on-dark)" : "#5E8068", borderRadius: 99, padding: "1px 7px", fontWeight: 700,
                  }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Reveal>

      {/* guides grid */}
      <Reveal><h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em", margin: "0 0 16px" }}>Guías</h2></Reveal>
      {loadingG ? (
        <div className="grid-4" style={{ marginBottom: 40 }}>
          {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton" style={{ aspectRatio: "3/4" }} />)}
        </div>
      ) : catGuias.length === 0 ? (
        <p style={{ color: "var(--muted)", fontSize: 14.5, padding: "1rem 0 2.5rem" }}>
          No hay guías aún para esta categoría.
        </p>
      ) : (
        <div className="grid-4" style={{ marginBottom: 40 }}>
          {catGuias.map((g, i) => (
            <Reveal key={g.id} delay={(i % 4) as 0 | 1 | 2 | 3}>
              <div className="card card--hover" style={{ overflow: "hidden", height: "100%" }}>
                <div style={{
                  position: "relative", aspectRatio: "3/4",
                  background: `repeating-linear-gradient(135deg, ${activeCat.bg}, ${activeCat.bg} 9px, #E8DBC9 9px, #E8DBC9 18px)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <img src="/logo-psicodosis.png" style={{ width: 44, height: 44, objectFit: "contain", opacity: 0.8 }} />
                  {isNew(g.created_at) && <span className="badge-new" style={{ position: "absolute", top: 12, left: 12 }}>NUEVO</span>}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: activeCat.text, marginBottom: 7 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: activeCat.dot }} />{activeCat.label}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3, marginBottom: 4 }}>{g.titulo}</div>
                  <a href={g.url_r2} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "var(--copper)", fontWeight: 700, textDecoration: "none" }}>
                    Descargar ↓
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      {/* support material */}
      <Reveal><h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em", margin: "0 0 16px" }}>Material de apoyo</h2></Reveal>
      {loadingM ? (
        <div className="grid-3">
          {[1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 90 }} />)}
        </div>
      ) : material.length === 0 ? (
        <p style={{ color: "var(--muted)", fontSize: 14.5, padding: "1rem 0" }}>
          Próximamente más material para esta categoría.
        </p>
      ) : (
        <div className="grid-3">
          {material.map((m, i) => (
            <Reveal key={m.id} delay={(i % 4) as 0 | 1 | 2 | 3}>
              <a href={m.url_r2} target="_blank" rel="noopener noreferrer"
                className="card card--hover" style={{ padding: 20, display: "flex", alignItems: "center", gap: 16, textDecoration: "none", color: "inherit", height: "100%" }}>
                <div style={{ width: 50, height: 50, borderRadius: 13, background: activeCat.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                  {m.tipo === "ejercicio" ? "🧩" : m.tipo === "plantilla" ? "📝" : "🎧"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{m.titulo}</div>
                  <div style={{ fontSize: 13, color: "var(--muted-2)" }}>{m.tipo}</div>
                </div>
                <span style={{ color: "var(--copper)", fontSize: 20, flexShrink: 0 }}>↓</span>
              </a>
            </Reveal>
          ))}
        </div>
      )}
    </PrivateLayout>
  );
}
